import type {
  WerkflowRequestMethod,
  WerkflowRestInvoker,
  WerkflowRestRequest,
  WerkflowRestRisk,
} from "./werkflowApi.ts";

export type WerkflowToolRisk = WerkflowRestRisk;
export type WerkflowToolArgType = "string" | "number" | "boolean" | "object" | "array";

export interface WerkflowToolArgSchemaField {
  readonly type: WerkflowToolArgType;
  readonly description: string;
  readonly required?: boolean;
  readonly enum?: ReadonlyArray<string>;
}

export interface WerkflowToolArgSchema {
  readonly fields: Readonly<Record<string, WerkflowToolArgSchemaField>>;
  readonly additionalFields?: boolean;
}

/**
 * A curated Aveda OS tool, mapped to a concrete `/api/v1` REST endpoint.
 * Path params in {@link pathTemplate} (e.g. `:leadId`) are filled from the
 * matching argument; the remaining args become the query string (GET) or the
 * JSON body (writes).
 */
export interface WerkflowToolDefinition {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly method: WerkflowRequestMethod;
  readonly pathTemplate: string;
  readonly risk: WerkflowToolRisk;
  readonly argsSchema: WerkflowToolArgSchema;
}

export interface CallWerkflowToolOptions {
  readonly approved?: boolean;
}

const noArgs: WerkflowToolArgSchema = { fields: {} };
const idField = (description: string): WerkflowToolArgSchemaField => ({
  type: "string",
  required: true,
  description,
});
const optionalString = (description: string): WerkflowToolArgSchemaField => ({
  type: "string",
  description,
});
const optionalBoolean = (description: string): WerkflowToolArgSchemaField => ({
  type: "boolean",
  description,
});
const optionalNumber = (description: string): WerkflowToolArgSchemaField => ({
  type: "number",
  description,
});
const requiredString = (description: string): WerkflowToolArgSchemaField => ({
  type: "string",
  required: true,
  description,
});
const optionalObject = (description: string): WerkflowToolArgSchemaField => ({
  type: "object",
  description,
});
const requiredArray = (description: string): WerkflowToolArgSchemaField => ({
  type: "array",
  required: true,
  description,
});
const enumField = (
  description: string,
  values: ReadonlyArray<string>,
  required = false,
): WerkflowToolArgSchemaField => ({
  type: "string",
  required,
  enum: values,
  description,
});

const leadSourceValues = ["Empfehlung", "Web", "Telefon", "Messe", "Sonstige"] as const;
const leadStageValues = ["Neu", "Qualifiziert", "Angebot gesendet"] as const;
const billingKindValues = ["Angebot", "Rechnung", "Gutschrift"] as const;

export const WERKFLOW_TOOL_DEFINITIONS: ReadonlyArray<WerkflowToolDefinition> = [
  {
    id: "leads.list",
    label: "List leads",
    description: "Find active leads by stage or free-text search.",
    method: "GET",
    pathTemplate: "/leads",
    risk: "read",
    argsSchema: {
      fields: {
        search: optionalString("Optional search across company, contact, email, and project."),
        stage: enumField("Optional pipeline stage filter.", leadStageValues),
        limit: optionalNumber("Optional maximum number of leads to return."),
        cursor: optionalString("Optional pagination cursor."),
      },
    },
  },
  {
    id: "leads.get",
    label: "Read lead",
    description: "Inspect one lead by document id.",
    method: "GET",
    pathTemplate: "/leads/:leadId",
    risk: "read",
    argsSchema: {
      fields: {
        leadId: idField("Convex lead document id."),
      },
    },
  },
  {
    id: "leads.create",
    label: "Create lead",
    description: "Capture a new inbound customer request.",
    method: "POST",
    pathTemplate: "/leads",
    risk: "low-risk-write",
    argsSchema: {
      fields: {
        company: requiredString("Lead company or household name."),
        contactName: optionalString("Primary contact name."),
        email: optionalString("Contact email address."),
        phone: optionalString("Contact phone number."),
        address: optionalString("Customer/project address."),
        project: optionalString("Short description of requested work."),
        source: enumField("Lead source.", leadSourceValues),
        expectedValue: optionalNumber("Expected gross value in EUR."),
        stage: enumField("Initial pipeline stage.", leadStageValues),
        notes: optionalString("Internal notes for the lead."),
      },
    },
  },
  {
    id: "leads.update",
    label: "Update lead",
    description: "Edit lead notes, contact details, or workflow fields.",
    method: "PATCH",
    pathTemplate: "/leads/:leadId",
    risk: "low-risk-write",
    argsSchema: {
      fields: {
        leadId: idField("Convex lead document id."),
        company: optionalString("Lead company or household name."),
        contactName: optionalString("Primary contact name."),
        email: optionalString("Contact email address."),
        phone: optionalString("Contact phone number."),
        address: optionalString("Customer/project address."),
        project: optionalString("Short description of requested work."),
        source: enumField("Lead source.", leadSourceValues),
        expectedValue: optionalNumber("Expected gross value in EUR."),
        notes: optionalString("Internal notes for the lead."),
      },
    },
  },
  {
    id: "leads.move",
    label: "Move lead",
    description: "Move a lead between pipeline stages.",
    method: "PATCH",
    pathTemplate: "/leads/:leadId",
    risk: "low-risk-write",
    argsSchema: {
      fields: {
        leadId: idField("Convex lead document id."),
        stage: enumField("Target pipeline stage.", leadStageValues, true),
      },
    },
  },
  {
    id: "leads.convert",
    label: "Convert lead",
    description: "Convert a qualified lead into customer and project records.",
    method: "POST",
    pathTemplate: "/leads/:leadId/convert",
    risk: "approval-required",
    argsSchema: {
      fields: {
        leadId: idField("Convex lead document id."),
        existingCustomerId: optionalString("Optional Convex customer id to merge into."),
      },
    },
  },
  {
    id: "customers.list",
    label: "List customers",
    description: "Find customer records, contacts, and addresses.",
    method: "GET",
    pathTemplate: "/customers",
    risk: "read",
    argsSchema: {
      fields: {
        search: optionalString(
          "Optional search across customer name, contact, email, and address.",
        ),
        includeArchived: optionalBoolean("Include archived customers in the result."),
        limit: optionalNumber("Optional maximum number of customers to return."),
        cursor: optionalString("Optional pagination cursor."),
      },
    },
  },
  {
    id: "projects.list",
    label: "List projects",
    description: "Find current jobs, tasks, schedules, and project status.",
    method: "GET",
    pathTemplate: "/projects",
    risk: "read",
    argsSchema: {
      fields: {
        stage: optionalString("Optional project stage filter."),
        limit: optionalNumber("Optional maximum number of projects to return."),
        cursor: optionalString("Optional pagination cursor."),
      },
    },
  },
  {
    id: "billing.list",
    label: "List billing documents",
    description: "Inspect offers, invoices, credit notes, and payment status.",
    method: "GET",
    pathTemplate: "/billing-documents",
    risk: "read",
    argsSchema: {
      fields: {
        kind: enumField("Optional billing document kind.", billingKindValues),
        status: optionalString("Optional status filter."),
        search: optionalString("Search by document number, customer, or project."),
      },
    },
  },
  {
    id: "billing.createDraft",
    label: "Create billing draft",
    description: "Prepare a draft offer or invoice for human review.",
    method: "POST",
    pathTemplate: "/billing-documents",
    risk: "approval-required",
    argsSchema: {
      fields: {
        kind: enumField("Billing document kind to create.", billingKindValues, true),
        customerRef: optionalString("Optional customer or lead legacy id/document id."),
        customerSnapshot: optionalObject("Optional explicit customer snapshot."),
        projectRef: optionalString("Optional project legacy id/document id."),
        project: optionalString("Project label on the draft."),
        responsible: optionalString("Responsible employee name."),
      },
    },
  },
  {
    id: "billing.updateDraft",
    label: "Update billing draft",
    description: "Change line items, notes, or metadata on a draft billing document.",
    method: "PATCH",
    pathTemplate: "/billing-documents/:documentId",
    risk: "approval-required",
    argsSchema: {
      fields: {
        documentId: idField("Convex billing document id."),
        customerSnapshot: {
          type: "object",
          required: true,
          description: "Customer snapshot with company, contactName, email, phone, and address.",
        },
        project: requiredString("Project label on the document."),
        issueDate: requiredString("Issue date, normally ISO YYYY-MM-DD."),
        responsible: requiredString("Responsible employee name."),
        items: requiredArray("Billing line items."),
        customerRef: optionalString("Optional customer or lead legacy id/document id."),
        projectRef: optionalString("Optional project legacy id/document id."),
        serviceDate: optionalString("Optional service date."),
        dueDate: optionalString("Optional invoice due date."),
        validUntil: optionalString("Optional offer validity date."),
        introText: optionalString("Optional intro text."),
        footerText: optionalString("Optional footer text."),
        note: optionalString("Optional internal note."),
        paymentTermsDays: optionalNumber("Optional payment terms in days."),
      },
    },
  },
  {
    id: "billing.finalize",
    label: "Finalize billing document",
    description: "Finalize a billing document that may become customer-facing.",
    method: "POST",
    pathTemplate: "/billing-documents/:documentId/finalize",
    risk: "approval-required",
    argsSchema: {
      fields: {
        documentId: idField("Convex billing document id."),
      },
    },
  },
  {
    id: "planning.get",
    label: "Read planning",
    description: "Inspect planned work, assignments, and schedule context.",
    method: "GET",
    pathTemplate: "/planning/full",
    risk: "read",
    argsSchema: noArgs,
  },
  {
    id: "inventory.list",
    label: "List inventory",
    description: "Find inventory items, stock levels, and material availability.",
    method: "GET",
    pathTemplate: "/inventory-items",
    risk: "read",
    argsSchema: {
      fields: {
        category: optionalString("Optional category filter."),
        lowStock: optionalBoolean("Only return items below their reorder point."),
        limit: optionalNumber("Optional maximum number of items to return."),
      },
    },
  },
  {
    id: "communication.listChannels",
    label: "List communication channels",
    description: "Find communication channels connected to ERP records.",
    method: "GET",
    pathTemplate: "/communication-channels",
    risk: "read",
    argsSchema: {
      fields: {
        status: optionalString("Optional channel status filter."),
      },
    },
  },
  {
    id: "settings.get",
    label: "Read ERP settings",
    description: "Inspect configured workspace defaults used by ERP workflows.",
    method: "GET",
    pathTemplate: "/settings",
    risk: "read",
    argsSchema: noArgs,
  },
  {
    id: "automations.catalog",
    label: "List automations catalog",
    description: "Browse the ERP automations catalog, including keys and enablement status.",
    method: "GET",
    pathTemplate: "/automations/catalog",
    risk: "read",
    argsSchema: noArgs,
  },
  {
    id: "automations.runs",
    label: "List automation runs",
    description: "Inspect recent automation run history and outcomes.",
    method: "GET",
    pathTemplate: "/automations/runs",
    risk: "read",
    argsSchema: {
      fields: {
        limit: optionalNumber("Optional maximum number of automation runs to return."),
      },
    },
  },
  {
    id: "automations.enable",
    label: "Enable automation",
    description: "Enable an ERP automation by its catalog key.",
    method: "POST",
    pathTemplate: "/automations/:key/enable",
    risk: "low-risk-write",
    argsSchema: {
      fields: {
        key: idField("Automation catalog key, for example offer_followup."),
      },
    },
  },
  {
    id: "automations.disable",
    label: "Disable automation",
    description: "Disable an ERP automation by its catalog key.",
    method: "POST",
    pathTemplate: "/automations/:key/disable",
    risk: "low-risk-write",
    argsSchema: {
      fields: {
        key: idField("Automation catalog key, for example offer_followup."),
      },
    },
  },
  {
    id: "automations.saveConfig",
    label: "Save automation configuration",
    description: "Persist configuration parameters for an ERP automation.",
    method: "POST",
    pathTemplate: "/automations/:key/save-config",
    risk: "approval-required",
    argsSchema: {
      fields: {
        key: idField("Automation catalog key, for example offer_followup."),
        params: optionalObject("Automation configuration parameters to persist."),
      },
    },
  },
];

export function findWerkflowToolDefinition(toolId: string): WerkflowToolDefinition | null {
  return WERKFLOW_TOOL_DEFINITIONS.find((tool) => tool.id === toolId) ?? null;
}

export function isWerkflowToolApprovalRequired(toolId: string): boolean {
  return findWerkflowToolDefinition(toolId)?.risk === "approval-required";
}

function valueMatchesType(value: unknown, type: WerkflowToolArgType): boolean {
  if (type === "array") {
    return Array.isArray(value);
  }
  if (type === "object") {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }
  return typeof value === type;
}

export function validateWerkflowToolArgs(
  tool: WerkflowToolDefinition,
  args: Record<string, unknown>,
): void {
  const schema = tool.argsSchema;
  for (const [fieldName, field] of Object.entries(schema.fields)) {
    const value = args[fieldName];
    if (value === undefined) {
      if (field.required) {
        throw new Error(`Aveda OS tool ${tool.id} missing required argument: ${fieldName}`);
      }
      continue;
    }
    if (!valueMatchesType(value, field.type)) {
      throw new Error(`Aveda OS tool ${tool.id} argument ${fieldName} must be ${field.type}`);
    }
    if (field.enum && typeof value === "string" && !field.enum.includes(value)) {
      throw new Error(
        `Aveda OS tool ${tool.id} argument ${fieldName} must be one of: ${field.enum.join(", ")}`,
      );
    }
  }

  if (schema.additionalFields !== false) {
    return;
  }
  const allowedFields = new Set(Object.keys(schema.fields));
  const unknownField = Object.keys(args).find((fieldName) => !allowedFields.has(fieldName));
  if (unknownField) {
    throw new Error(`Aveda OS tool ${tool.id} received unknown argument: ${unknownField}`);
  }
}

export type WerkflowToolRequest = WerkflowRestRequest;

/**
 * Build the concrete REST request for a curated tool: fill path params from
 * args, then route the remaining args to the query string (GET) or JSON body
 * (writes).
 */
export function buildWerkflowToolRequest(
  tool: WerkflowToolDefinition,
  args: Record<string, unknown>,
): WerkflowToolRequest {
  const usedParams = new Set<string>();
  const path = tool.pathTemplate
    .split("/")
    .map((segment) => {
      if (!segment.startsWith(":")) return segment;
      const paramName = segment.slice(1);
      const value = args[paramName];
      if (value === undefined || value === null || value === "") {
        throw new Error(`Aveda OS tool ${tool.id} missing path parameter: ${paramName}`);
      }
      usedParams.add(paramName);
      return encodeURIComponent(String(value));
    })
    .join("/");

  const rest: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(args)) {
    if (usedParams.has(key)) continue;
    if (value === undefined) continue;
    rest[key] = value;
  }

  if (tool.method === "GET") {
    return {
      method: tool.method,
      path,
      query: rest as Record<string, string | number | boolean | undefined | null>,
    };
  }
  if (Object.keys(rest).length > 0) {
    return { method: tool.method, path, body: rest };
  }
  return { method: tool.method, path };
}

export async function callWerkflowTool(
  invoker: WerkflowRestInvoker,
  toolId: string,
  args: Record<string, unknown>,
  options: CallWerkflowToolOptions = {},
): Promise<unknown> {
  const tool = findWerkflowToolDefinition(toolId);
  if (!tool) {
    throw new Error(`Unknown Aveda OS tool: ${toolId}`);
  }
  validateWerkflowToolArgs(tool, args);
  if (tool.risk === "approval-required" && options.approved !== true) {
    throw new Error(`Aveda OS tool requires approval: ${toolId}`);
  }
  const request = buildWerkflowToolRequest(tool, args);
  const result = await invoker.request(request);
  return result.value;
}

/**
 * Normalize a raw ERP base URL to a canonical origin (trailing slashes,
 * query, and hash removed). Retained for settings/UI helpers.
 */
export function normalizeConvexDeploymentUrl(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  if (!trimmed) {
    throw new Error("Aveda OS URL is required.");
  }
  const url = new URL(trimmed);
  url.pathname = url.pathname.replace(/\/+$/, "");
  url.search = "";
  url.hash = "";
  return url.toString().replace(/\/+$/, "");
}
